import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Virtuoso } from "react-virtuoso";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  AdminReviewSessionViewDto,
  GfFlyerReviewComment,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { useTranslation } from "react-i18next";
import { reviewApiService } from "../../../../../utils/api";
import { extractList } from "../../types";
import { reviewKeys } from "../../queryKeys";
import { CommentsToolbar } from "../../styled";
import {
  COMMENT_STATUS,
  commentStatusOf,
  commentTargetOf,
} from "./commentTypes";
import { CommentsFilterBar, CommentStatusFilter } from "./CommentsFilterBar";
import { CommentThread } from "./CommentThread";
import {
  threadMatchesSearch,
  useCommentThreads,
} from "./useCommentThreads";
import {
  useAuthorResolver,
  useResolvedByResolver,
} from "./useAuthorResolver";

interface Props {
  flyerId: string;
  reqOptions: { headers: { Authorization: string } };
  bootstrap: AdminReviewSessionViewDto | undefined;
  targetTypeFilter?: "FLYER" | "FLYER_ITEM" | "FIELD";
  commentsOverride?: GfFlyerReviewComment[];
}

const EMPTY_COMMENTS: GfFlyerReviewComment[] = [];

// Item-level comment lists are usually tiny, so only the (potentially large)
// flyer-level drawer crosses this threshold and gets windowed.
const THREAD_VIRTUALIZE_THRESHOLD = 30;

export function CommentsPanel({
  flyerId,
  reqOptions,
  bootstrap,
  targetTypeFilter,
  commentsOverride,
}: Props) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<CommentStatusFilter>("OPEN");
  const [search, setSearch] = useState("");
  const auth = reqOptions.headers.Authorization;

  // When `commentsOverride` is supplied (item-level usage) the parent owns the
  // data, so this query stays disabled. Otherwise (flyer-level drawer) the
  // comments share `reviewKeys.comments`, so a comment socket event
  // invalidating that key refreshes the drawer with no manual signal.
  const commentsQuery = useQuery({
    queryKey: reviewKeys.comments(flyerId),
    queryFn: ({ signal }) =>
      reviewApiService.gfFlyerReviewAdminControllerListComments(
        flyerId,
        undefined,
        undefined,
        undefined,
        { headers: { Authorization: auth }, signal },
      ),
    enabled: commentsOverride === undefined && !!flyerId,
    staleTime: 0,
    gcTime: 5 * 60_000,
    select: (res) => extractList<GfFlyerReviewComment>(res),
  });

  const fetchedComments = commentsQuery.data ?? EMPTY_COMMENTS;
  const initialLoading =
    commentsOverride === undefined && commentsQuery.isLoading;
  const refreshing =
    commentsOverride === undefined &&
    !commentsQuery.isLoading &&
    commentsQuery.isFetching;

  const sourceComments = useMemo(() => {
    const base = commentsOverride ?? fetchedComments;
    if (!targetTypeFilter) return base;
    return base.filter((c) => commentTargetOf(c) === targetTypeFilter);
  }, [commentsOverride, fetchedComments, targetTypeFilter]);

  const threads = useCommentThreads(sourceComments);
  const audiences = useMemo(
    () => bootstrap?.audiences ?? [],
    [bootstrap?.audiences],
  );
  const resolveAuthor = useAuthorResolver(audiences);
  const resolveResolvedBy = useResolvedByResolver(audiences);

  const counts = useMemo(() => {
    let open = 0;
    let resolved = 0;
    for (const th of threads) {
      if (commentStatusOf(th.root) === COMMENT_STATUS.RESOLVED) resolved += 1;
      else open += 1;
    }
    return { all: threads.length, open, resolved };
  }, [threads]);

  const visibleThreads = useMemo(() => {
    const byStatus =
      statusFilter === "ALL"
        ? threads
        : threads.filter((th) => commentStatusOf(th.root) === statusFilter);
    const needle = search.trim().toLowerCase();
    if (!needle) return byStatus;
    return byStatus.filter((th) =>
      threadMatchesSearch(th, needle, resolveAuthor),
    );
  }, [threads, statusFilter, search, resolveAuthor]);

  if (initialLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  const showEmptyAll = threads.length === 0;
  const showEmptyFiltered = !showEmptyAll && visibleThreads.length === 0;

  let emptyLabel = "";
  if (showEmptyAll) {
    emptyLabel = t(messages.No_Comments_Yet());
  } else if (showEmptyFiltered) {
    if (search.trim()) {
      emptyLabel = t(messages.No_Comments_Match_Search());
    } else if (statusFilter === "OPEN") {
      emptyLabel = t(messages.No_Open_Comments());
    } else if (statusFilter === "RESOLVED") {
      emptyLabel = t(messages.No_Resolved_Comments());
    }
  }

  return (
    <Box data-testid="comments-panel">
      <CommentsToolbar>
        <CommentsFilterBar
          status={statusFilter}
          onStatusChange={setStatusFilter}
          counts={counts}
          search={search}
          onSearchChange={setSearch}
          refreshing={refreshing}
        />
      </CommentsToolbar>
      {showEmptyAll || showEmptyFiltered ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        </Stack>
      ) : visibleThreads.length > THREAD_VIRTUALIZE_THRESHOLD ? (
        <Virtuoso
          data={visibleThreads}
          computeItemKey={(_, th) => th.root.id}
          overscan={600}
          style={{ height: "70vh" }}
          itemContent={(_, th) => (
            <CommentThread
              thread={th}
              resolveAuthor={resolveAuthor}
              resolveResolvedBy={resolveResolvedBy}
            />
          )}
        />
      ) : (
        <Box>
          {visibleThreads.map((th) => (
            <CommentThread
              key={th.root.id}
              thread={th}
              resolveAuthor={resolveAuthor}
              resolveResolvedBy={resolveResolvedBy}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
